(() => {
  const SECTION_SELECTOR = '.insieme-v2[data-section-id]';
  const ADD_BUTTON_SELECTOR = '[data-insieme-add-to-cart]';
  const CHUTNEY_SELECTOR = '[data-insieme-chutney-card]';

  function getNumber(value, fallback = 0) {
    const number = Number.parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  }

  function getChutneySelection(section, includedLimit) {
    let remainingIncluded = Math.max(0, includedLimit);
    const included = [];
    const extra = [];

    Array.from(section.querySelectorAll(CHUTNEY_SELECTOR)).forEach((card) => {
      const quantity = getNumber(card.dataset.chutneyQuantity, 0);
      const name = card.dataset.chutneyName || '';

      if (quantity <= 0 || !name) return;

      const includedQuantity = Math.min(quantity, remainingIncluded);
      const extraQuantity = Math.max(0, quantity - includedQuantity);

      if (includedQuantity > 0) {
        included.push(`${name} × ${includedQuantity}`);
        remainingIncluded -= includedQuantity;
      }

      if (extraQuantity > 0) {
        extra.push(`${name} × ${extraQuantity}`);
      }
    });

    return {
      included: included.join(' · '),
      extra: extra.join(' · ')
    };
  }

  function getAccompanimentData(section) {
    const root = section.querySelector('[data-insieme-accompaniments]');
    const people = getNumber(root?.dataset.people || section.dataset.people, 0);
    const glutenFree = Math.min(
      people,
      Math.max(0, getNumber(root?.dataset.glutenFreePortions, 0))
    );
    const standard = Math.max(0, people - glutenFree);
    const standardLabel = root?.dataset.standardLabel || 'Classici';
    const glutenFreeLabel = root?.dataset.glutenFreeLabel || 'Senza glutine';
    const summary = [];

    if (standard > 0) summary.push(`${standardLabel} × ${standard}`);
    if (glutenFree > 0) summary.push(`${glutenFreeLabel} × ${glutenFree}`);

    return {
      people,
      standard,
      glutenFree,
      summary: summary.join(' · ')
    };
  }

  function showMessage(section, message, type = 'error') {
    const messageElement = section.querySelector(
      '[data-insieme-cart-message]'
    );

    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.dataset.state = type;
    messageElement.hidden = false;
    messageElement.setAttribute('tabindex', '-1');
    messageElement.focus({
      preventScroll: true
    });
  }

  function hideMessage(section) {
    const messageElement = section.querySelector(
      '[data-insieme-cart-message]'
    );

    if (!messageElement) return;

    messageElement.hidden = true;
    messageElement.textContent = '';
    delete messageElement.dataset.state;
  }

  function setLoading(button, loading) {
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
    button.classList.toggle('is-loading', loading);
  }

  async function addToCart(section, button) {
    if (section.dataset.cartSubmitting === 'true') return;

    hideMessage(section);

    const requiredChutneys = getNumber(
      section.dataset.requiredChutneys,
      0
    );

    const selectedChutneys = getNumber(
      section.dataset.selectedChutneys,
      0
    );

    const missingChutneys = Math.max(
      0,
      requiredChutneys - selectedChutneys
    );

    if (missingChutneys > 0) {
      const configuredMessage =
        section.dataset.incompleteMessage ||
        'Completa la selezione dei chutney prima di continuare.';

      const message =
        missingChutneys === 1
          ? configuredMessage
          : `Seleziona ancora ${missingChutneys} chutney inclusi per continuare.`;

      showMessage(section, message);
      return;
    }

    const variantInput = section.querySelector(
      '[data-insieme-variant-input]'
    );

    const variantId = getNumber(variantInput?.value, 0);
    // Ogni Tagliere Insieme e la relativa composizione devono occupare
    // una riga autonoma nel carrello. Una quantità maggiore di 1 farebbe
    // condividere la stessa proprietà Chutney a più taglieri.
    const productQuantity = 1;
    section.dataset.quantity = '1';

    if (!variantId) {
      showMessage(
        section,
        section.dataset.cartErrorMessage ||
          'Non è stato possibile identificare il formato selezionato.'
      );

      return;
    }

    const extraQuantity = getNumber(
      section.dataset.extraChutneys,
      0
    );

    const extraVariantId = getNumber(
      section.dataset.extraChutneyVariantId,
      0
    );

    if (extraQuantity > 0 && !extraVariantId) {
      showMessage(
        section,
        section.dataset.extraConfigMessage ||
          'Configura il prodotto Shopify per i chutney extra.'
      );

      return;
    }

    const chutneys = getChutneySelection(section, requiredChutneys);
    const accompaniments = getAccompanimentData(section);
    const groupId = `${variantId}-${Date.now()}`;
    const selectedFormat = section.querySelector(
      '[data-insieme-format-card].is-selected'
    );
    const formatTitle =
      selectedFormat?.querySelector('strong')?.textContent?.trim() ||
      section.dataset.productTitle ||
      'Tagliere Insieme';

    const parentProperties = {
      'Chutney inclusi': chutneys.included,
      Accompagnamenti: accompaniments.summary,
      _Gruppo: groupId
    };

    if (chutneys.extra) {
      parentProperties['Chutney extra'] = chutneys.extra;
    }

    const parentItem = {
      id: variantId,
      quantity: productQuantity,
      properties: parentProperties
    };

    const items = [];

    if (extraQuantity > 0) {
      items.push({
        id: extraVariantId,
        quantity: extraQuantity,
        properties: {
          'Extra abbinato a': formatTitle,
          _Gruppo: groupId
        }
      });
    }

    // Shopify mostra spesso per prima l'ultima riga aggiunta. Inserendo il
    // tagliere dopo gli extra, nel backend il prodotto principale rimane in alto.
    items.push(parentItem);

    section.dataset.cartSubmitting = 'true';
    setLoading(button, true);

    const controller = new AbortController();

    const timeout = window.setTimeout(() => {
      controller.abort();
    }, 15000);

    try {
      const response = await fetch(
        section.dataset.cartAddUrl || '/cart/add.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items }),
          signal: controller.signal
        }
      );

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
          result.message ||
          'Impossibile aggiungere i prodotti al carrello.'
        );
      }

      window.location.href =
        section.dataset.cartUrl || '/cart';
    } catch (error) {
      const message =
        error.name === 'AbortError'
          ? 'Il carrello sta impiegando troppo tempo. Controlla il carrello prima di riprovare.'
          : error.message ||
            section.dataset.cartErrorMessage ||
            'Si è verificato un errore. Riprova.';

      showMessage(section, message);
      section.dataset.cartSubmitting = 'false';
      setLoading(button, false);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(
      ADD_BUTTON_SELECTOR
    );

    if (!button) return;

    const section = button.closest(SECTION_SELECTOR);

    if (!section) return;

    event.preventDefault();
    addToCart(section, button);
  });

  document.addEventListener(
    'insieme:variant-change',
    (event) => hideMessage(event.target)
  );

  document.addEventListener(
    'insieme:quantity-change',
    (event) => hideMessage(event.target)
  );

  document.addEventListener(
    'insieme:chutney-change',
    (event) => hideMessage(event.target)
  );

  document.addEventListener(
    'insieme:accompaniment-change',
    (event) => hideMessage(event.target)
  );
})();
